import { create } from "zustand";

export interface Permission {
  code: string;
  module: string;
  action: string;
  displayName: string;
}

export interface UserOrgItem {
  id?: string;
  name: string;
  shortName?: string;
}

export interface UserGroupItem {
  name: string;
  dn?: string;
}

export interface AuthUser {
  accessToken?: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
  userId: string;
  username: string;
  email: string;
  displayName: string;
  orgId: string | null;
  orgName: string | null;
  roles: string[];
  identitySource: "LDAP" | "LOCAL" | "OIDC" | "ACTIVE_DIRECTORY";
  permissions?: Permission[];
  organizations?: Array<UserOrgItem | string>;
  groups?: Array<UserGroupItem | string>;
  adGroups?: Array<UserGroupItem | string>;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (user: AuthUser) => void;
  logout: () => void;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  setPermissions: (permissions: Permission[] | string[]) => void;
  hasPermission: (module: string, action: string) => boolean;
  hasPermissionsLoaded: () => boolean;
  updateTokens: (accessToken: string, refreshToken?: string) => void;
  setProfileDetails: (details: { organizations: UserOrgItem[]; groups: UserGroupItem[] }) => void;
}

/* Stores that hold user data in memory register here so logout can clear them.
   Logout is a client-side navigate(), not a page load, so module-level stores otherwise
   survive into the next user's session. A registry keeps this file free of cycles. */
const sessionResetHandlers = new Set<() => void>();

/** Registers a reset to run on logout. Returns an unsubscribe for tests. */
export function onSessionReset(handler: () => void): () => void {
  sessionResetHandlers.add(handler);
  return () => sessionResetHandlers.delete(handler);
}

function runSessionResets(): void {
  for (const handler of sessionResetHandlers) {
    try {
      handler();
    } catch (error) {
      console.error("Session reset handler failed:", error);
    }
  }
}

/**
 * Unified storage key for all auth/session data.
 * All auth data lives strictly in sessionStorage (isolated per tab, destroyed on tab close).
 */
const STORAGE_KEY = "auth_user";
const LEGACY_STORAGE_PERSIST_KEY = "auth_user_persist";

/** Clean up any legacy cookies and localStorage items from previous versions */
export function cleanupLegacyStorage(): void {
  if (typeof window === "undefined") return;

  // Clean legacy localStorage keys
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_STORAGE_PERSIST_KEY);
    }
  } catch (err) {
    console.warn("[AuthStore] Error cleaning legacy localStorage:", err);
  }

  // Clean legacy document.cookie tokens
  try {
    if (typeof document !== "undefined") {
      const names = [
        "accessToken",
        "token",
        "access_token",
        "jwt",
        "refreshToken",
        "refresh_token",
        "Authorization"
      ];
      const isSecure = window.location.protocol === "https:";
      for (const name of names) {
        if (isSecure) {
          document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=None`;
        }
        document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
      }
    }
  } catch (err) {
    console.warn("[AuthStore] Error cleaning legacy cookies:", err);
  }
}

/** Load user session exclusively from sessionStorage */
function loadPersistedUser(): AuthUser | null {
  cleanupLegacyStorage();

  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch (error) {
    console.error("Failed to load user from sessionStorage:", error);
    return null;
  }
}

/** Kept for backward compatibility with AuthGuard */
export function restoreRememberedUser(): AuthUser | null {
  return null;
}

/** Backward compatibility stubs for cookie helpers */
export function setAuthCookies(_accessToken: string, _refreshToken?: string): void {
  // Deprecated: Auth tokens are managed exclusively via sessionStorage
}

export function getAuthCookie(_name?: string): string | null {
  return useAuthStore.getState().getToken();
}

export function clearAuthCookies(): void {
  cleanupLegacyStorage();
}

const initialUser = loadPersistedUser();

export const useAuthStore = create<AuthState>((set, get) => ({
  user: initialUser,
  isAuthenticated: !!initialUser,

  login: (user: AuthUser) => {
    // Persist full user payload (profile + tokens) strictly in sessionStorage
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      }
    } catch (err) {
      console.warn("[AuthStore] Failed to write user to sessionStorage:", err);
    }

    set({ user, isAuthenticated: true });
  },

  logout: () => {
    // Clear active session from sessionStorage
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.warn("[AuthStore] Failed to clear sessionStorage:", err);
    }

    // Clean any legacy cookies or localStorage
    cleanupLegacyStorage();

    set({ user: null, isAuthenticated: false });
    runSessionResets();
  },

  getToken: () => {
    const user = get().user;
    if (user?.accessToken) return user.accessToken;

    try {
      if (typeof sessionStorage !== "undefined") {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          return parsed?.accessToken || null;
        }
      }
    } catch {
      return null;
    }
    return null;
  },

  getRefreshToken: () => {
    const user = get().user;
    if (user?.refreshToken) return user.refreshToken;

    try {
      if (typeof sessionStorage !== "undefined") {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          return parsed?.refreshToken || null;
        }
      }
    } catch {
      return null;
    }
    return null;
  },

  setPermissions: (permissionsInput: Permission[] | string[]) => {
    const { user } = get();
    if (user) {
      const permissions: Permission[] = (permissionsInput || []).map((perm: any) => {
        if (typeof perm === "string") {
          const [module, action] = perm.split(":");
          return { code: perm, module: module || "", action: action || "", displayName: `${module} ${action}` };
        }
        if (perm && typeof perm === "object") {
          const code = perm.code || (perm.module && perm.action ? `${perm.module}:${perm.action}` : "");
          let module = perm.module;
          let action = perm.action;
          if ((!module || !action) && code && typeof code === "string" && code.includes(":")) {
            const [m, a] = code.split(":");
            module = module || m;
            action = action || a;
          }
          return {
            ...perm,
            code: code || (module && action ? `${module}:${action}` : ""),
            module: module || "",
            action: action || "",
            displayName: perm.displayName || `${module || ""} ${action || ""}`.trim(),
          };
        }
        return perm;
      });
      const updatedUser = { ...user, permissions };
      try {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
        }
      } catch (err) {
        console.warn("[AuthStore] Failed to update permissions in sessionStorage:", err);
      }
      set({ user: updatedUser });
    }
  },

  hasPermission: (module: string, action: string) => {
    const { user } = get();
    // If permissions not loaded yet (undefined), allow all
    if (!user?.permissions) return true;
    // If permissions loaded but empty array, allow all (full access)
    if (user.permissions.length === 0) return true;
    // If permissions loaded with data, check specific permission
    return user.permissions.some((p) => p.module === module && p.action === action);
  },

  hasPermissionsLoaded: () => {
    const { user } = get();
    return user?.permissions !== undefined;
  },

  updateTokens: (accessToken: string, refreshToken?: string) => {
    const { user } = get();
    if (!user) return;
    const updatedUser: AuthUser = {
      ...user,
      accessToken,
      ...(refreshToken ? { refreshToken } : {}),
    };
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
      }
    } catch (err) {
      console.warn("[AuthStore] Failed to update tokens in sessionStorage:", err);
    }
    set({ user: updatedUser });
  },

  setProfileDetails: (details: { organizations: UserOrgItem[]; groups: UserGroupItem[] }) => {
    const { user } = get();
    if (user) {
      const updatedUser: AuthUser = {
        ...user,
        organizations: details.organizations,
        groups: details.groups,
      };
      try {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
        }
      } catch (err) {
        console.warn("[AuthStore] Failed to update profile details in sessionStorage:", err);
      }
      set({ user: updatedUser });
    }
  },
}));
